/**
 * Founder metrics page — cross-user North-star + funnel counts.
 *
 * Personal admin view (not per-user like the member Dashboard). Gated by
 * <AdminRoute> in App.tsx; the backend independently enforces require_admin,
 * so a non-admin hitting the API directly still gets 403.
 *
 * Deliberately minimal: counts, not charts. The deposit-test signal lives in
 * Stripe, not here — this tracks members in the product (and, post-GO, the
 * early-career cohort moving through the funnel).
 */

import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  getAdminMetricsSummary,
  AdminFunnelCounts,
  AdminMetricsRange,
  getAdminOpsMetrics,
  AdminOpsMetrics,
  getAdminNavigationMetrics,
  AdminNavigationMetrics,
  AdminNavigationRoute,
  getAdminContentMetrics,
  AdminContentMetrics,
  getAdminProductMetrics,
  AdminProductMetrics,
} from "../api/client";

interface Metric {
  label: string;
  value: string;
  hint?: string;
  highlight?: boolean; // the North-star gets visual weight
}

function MetricCard({ m }: { m: Metric }) {
  const card = (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        borderColor: m.highlight ? "primary.main" : "divider",
        borderWidth: m.highlight ? 2 : 1,
      }}
    >
      <CardContent>
        <Typography variant="overline" color="text.secondary">
          {m.label}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 600, mt: 0.5 }}>
          {m.value}
        </Typography>
        {m.hint && (
          <Typography variant="caption" color="text.secondary">
            {m.hint}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
  return m.hint ? <Tooltip title={m.hint}>{card}</Tooltip> : card;
}

function OperationsPanel() {
  const { t } = useTranslation("pages-core");
  const [ops, setOps] = useState<AdminOpsMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getAdminOpsMetrics();
        if (!cancelled) setOps(res);
      } catch (e) {
        if (!cancelled) setError(t("AdminMetricsPage.opsLoadErrorMessage"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !ops) {
    return <Typography color="error">{error ?? t("AdminMetricsPage.noOpsDataMessage")}</Typography>;
  }

  const spend = ops.agents.daily.reduce((sum, d) => sum + d.cost_usd, 0);
  const runs = ops.agents.daily.reduce((sum, d) => sum + d.runs, 0);
  const tiles: Metric[] = [
    {
      label: t("AdminMetricsPage.agentSpendLabel", { days: ops.agents.window_days }),
      value: `$${spend.toFixed(2)}`,
      hint: t("AdminMetricsPage.agentSpendHint"),
    },
    {
      label: t("AdminMetricsPage.agentRunsLabel", { days: ops.agents.window_days }),
      value: String(runs),
    },
    {
      label: t("AdminMetricsPage.agentErrorsLabel", { days: ops.agents.window_days }),
      value: String(ops.agents.status_mix.error),
      hint: t("AdminMetricsPage.agentErrorsHint", {
        ok: ops.agents.status_mix.ok,
        error: ops.agents.status_mix.error,
        killed: ops.agents.status_mix.killed,
        capped: ops.agents.status_mix.capped,
      }),
    },
    { label: t("AdminMetricsPage.coachMessagesLabel"), value: String(ops.coach.messages_7d) },
    { label: t("AdminMetricsPage.nudgesSentLabel"), value: String(ops.nudges.sent_7d) },
  ];

  return (
    <Box sx={{ mt: 5 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        {t("AdminMetricsPage.operationsHeading")}
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {tiles.map((m) => (
          <Grid item xs={12} sm={6} md={4} key={m.label}>
            <MetricCard m={m} />
          </Grid>
        ))}
      </Grid>

      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        {t("AdminMetricsPage.perAgentDailyHeading")}
      </Typography>
      {ops.agents.daily.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("AdminMetricsPage.noAgentRunsMessage", { days: ops.agents.window_days })}
        </Typography>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("AdminMetricsPage.dateColumn")}</TableCell>
                <TableCell>{t("AdminMetricsPage.agentColumn")}</TableCell>
                <TableCell align="right">{t("AdminMetricsPage.runsColumn")}</TableCell>
                <TableCell align="right">{t("AdminMetricsPage.errorsColumn")}</TableCell>
                <TableCell align="right">{t("AdminMetricsPage.costUsdColumn")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ops.agents.daily.map((d) => (
                <TableRow key={`${d.date}-${d.agent_name}`}>
                  <TableCell>{d.date}</TableCell>
                  <TableCell>{d.agent_name}</TableCell>
                  <TableCell align="right">{d.runs}</TableCell>
                  <TableCell align="right">{d.errors}</TableCell>
                  <TableCell align="right">${d.cost_usd.toFixed(4)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

/**
 * §1 Navigation — which pages lose people. Data is fetched by the parent in
 * the SAME effect (and with the SAME range) as the funnel summary, so the two
 * panels can never disagree about the window they describe; this component is
 * purely presentational.
 *
 * A suppressed cell and a genuine zero must never look the same: every
 * numeric field here is checked for `=== null`, not falsiness, before it is
 * rendered as the suppressed/unavailable treatment.
 */
/** Engaged-time cell: privacy suppression, missing timing data, and a real
 *  value are three different answers and must not share one glyph. */
function renderEngaged(
  r: AdminNavigationRoute,
  value: number | null,
  t: (k: string, o?: Record<string, unknown>) => string,
) {
  if (value !== null) return formatEngagedMs(value);
  if (!r.suppressed && r.percentiles_suppressed) {
    return (
      <Tooltip title={t("AdminMetricsPage.navNoTimingDataTooltip")}>
        <Typography
          component="span"
          variant="body2"
          color="text.secondary"
          data-testid="nav-no-timing"
        >
          {t("AdminMetricsPage.navNoTimingData")}
        </Typography>
      </Tooltip>
    );
  }
  return t("AdminMetricsPage.navSuppressed");
}

function NavigationPanel({
  nav,
  error,
}: {
  nav: AdminNavigationMetrics | null;
  error: string | null;
}) {
  const { t } = useTranslation("pages-core");

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }
  if (!nav) {
    return null; // still loading — the funnel above already shows a spinner
  }

  // Unavailable, never 0%: either the session-level cell was suppressed
  // outright, or the denominator was zero (dead_end_rate_pct is then null too).
  const deadEndUnavailable = nav.sessions_suppressed || nav.dead_end_rate_pct === null;

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        {t("AdminMetricsPage.navigationHeading")}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
        {t("AdminMetricsPage.navPartialWarning")}
      </Typography>
      {/* A silent exclusion is how a period with a broken client clock gets
          mistaken for a quiet one. Only rendered when non-zero — a permanent
          "0 events excluded" line is noise that trains the reader to skip it. */}
      {nav.skew_dropped > 0 && (
        <Typography
          variant="caption"
          color="warning.main"
          sx={{ display: "block", mb: 2 }}
          data-testid="nav-skew-dropped"
        >
          {t("AdminMetricsPage.navSkewDroppedWarning", { count: nav.skew_dropped })}
        </Typography>
      )}
      <Typography variant="body2" sx={{ mb: 0.5 }} data-testid="dead-end-rate">
        {t("AdminMetricsPage.navDeadEndLabel")}:{" "}
        {deadEndUnavailable || nav.dead_end_rate_pct === null
          ? t("AdminMetricsPage.navUnavailable")
          : `${nav.dead_end_rate_pct.toFixed(1)}%`}
        {/* A bare rate hides its own confidence: 12% of 25 sessions and 12% of
            2500 warrant different decisions. Both counts are null together
            whenever the session cell is suppressed, so this never narrows a
            suppressed cohort. */}
        {!deadEndUnavailable &&
          nav.dead_end_sessions !== null &&
          nav.total_sessions !== null && (
            <Typography component="span" variant="body2" color="text.secondary">
              {" "}
              {t("AdminMetricsPage.navDeadEndDenominator", {
                dead: nav.dead_end_sessions,
                total: nav.total_sessions,
              })}
            </Typography>
          )}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
        {t("AdminMetricsPage.navDeadEndCaption")}
      </Typography>
      {nav.routes.length === 0 ? (
        <Typography variant="body2">{t("AdminMetricsPage.navEmpty")}</Typography>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("AdminMetricsPage.navRouteColumn")}</TableCell>
                <TableCell align="right">{t("AdminMetricsPage.navViewsColumn")}</TableCell>
                <TableCell align="right">{t("AdminMetricsPage.navExitRateColumn")}</TableCell>
                <TableCell align="right">{t("AdminMetricsPage.navMedianTimeColumn")}</TableCell>
                <TableCell align="right">{t("AdminMetricsPage.navP90TimeColumn")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {nav.routes.map((r) => (
                <TableRow key={r.route_template}>
                  <TableCell>{r.route_template}</TableCell>
                  <TableCell align="right">
                    {r.views === null ? t("AdminMetricsPage.navSuppressed") : r.views}
                  </TableCell>
                  {/* An anomalous row also has a null exit_rate_pct, so without
                      this branch it renders identically to privacy suppression —
                      defeating the backend's stated intent of marking the row
                      "distinguishable from an ordinary suppressed cell". */}
                  <TableCell align="right">
                    {r.anomaly === "exits_exceed_views" ? (
                      <Tooltip title={t("AdminMetricsPage.navAnomalyTooltip")}>
                        <Typography
                          component="span"
                          variant="body2"
                          color="warning.main"
                          data-testid="nav-anomaly"
                        >
                          {t("AdminMetricsPage.navAnomaly")}
                        </Typography>
                      </Tooltip>
                    ) : r.exit_rate_pct === null ? (
                      t("AdminMetricsPage.navSuppressed")
                    ) : (
                      `${r.exit_rate_pct.toFixed(1)}%`
                    )}
                  </TableCell>
                  {/* Three distinct states, not two: privacy-suppressed (em
                      dash), too-few-timing-samples (labelled), or a value.
                      Collapsing the middle case into the em dash is what made
                      "withheld" and "never measured" look identical. */}
                  <TableCell align="right">
                    {renderEngaged(r, r.engaged_ms_median, t)}
                  </TableCell>
                  <TableCell align="right">
                    {renderEngaged(r, r.engaged_ms_p90, t)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

// Sub-second durations must render in ms, not a rounded-to-zero "0s" — a
// nonzero value displayed as zero is the cardinal rule this exists to avoid.
function formatEngagedMs(ms: number): string {
  return ms < 1000 ? `${Math.round(ms)}ms` : `${Math.round(ms / 1000)}s`;
}

/**
 * §2 Content — D4. Which modules get abandoned, and where.
 *
 * Fetched by the parent alongside the other panels so all three describe the
 * same window. A load failure degrades only this panel.
 */
function ContentPanel({
  content,
  error,
}: {
  content: AdminContentMetrics | null;
  error: string | null;
}) {
  const { t } = useTranslation("pages-core");

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          {t("AdminMetricsPage.contentHeading")}
        </Typography>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }
  if (!content) return null;

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        {t("AdminMetricsPage.contentHeading")}
      </Typography>
      {/* Both limits are structural, not transient, so they are stated every
          time rather than only when a number looks odd. */}
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        {t("AdminMetricsPage.contentPartialWarning", { product: content.product })}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
        {t("AdminMetricsPage.contentAnonNote")}
      </Typography>

      {content.modules.length === 0 ? (
        <Typography variant="body2">{t("AdminMetricsPage.contentEmpty")}</Typography>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("AdminMetricsPage.contentModuleColumn")}</TableCell>
                <TableCell align="right">{t("AdminMetricsPage.contentStartsColumn")}</TableCell>
                <TableCell align="right">{t("AdminMetricsPage.contentCompletionsColumn")}</TableCell>
                <TableCell align="right">{t("AdminMetricsPage.contentRateColumn")}</TableCell>
                <TableCell align="right">{t("AdminMetricsPage.contentAnonColumn")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {content.modules.map((m) => (
                <TableRow key={m.module_slug}>
                  <TableCell>{m.module_slug}</TableCell>
                  <TableCell align="right">
                    {m.starts === null ? t("AdminMetricsPage.navSuppressed") : m.starts}
                  </TableCell>
                  <TableCell align="right">
                    {m.completions === null
                      ? t("AdminMetricsPage.navSuppressed")
                      : m.completions}
                  </TableCell>
                  {/* A module nobody finished is a real, actionable 0% and the
                      most useful cell here — it must never render as the
                      suppression glyph. */}
                  <TableCell align="right" data-testid={`content-rate-${m.module_slug}`}>
                    {m.anomaly === "completions_exceed_starts" ? (
                      <Tooltip title={t("AdminMetricsPage.contentAnomalyTooltip")}>
                        <Typography
                          component="span"
                          variant="body2"
                          color="warning.main"
                          data-testid="content-anomaly"
                        >
                          {t("AdminMetricsPage.contentAnomaly")}
                        </Typography>
                      </Tooltip>
                    ) : m.completion_rate_pct === null ? (
                      t("AdminMetricsPage.navSuppressed")
                    ) : (
                      `${m.completion_rate_pct.toFixed(1)}%`
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {m.anonymous_opens === null
                      ? t("AdminMetricsPage.navSuppressed")
                      : m.anonymous_opens}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

/**
 * §3 Products — D7. Which products acquire, and which retain.
 *
 * The interesting failure this panel avoids: a range shorter than the return
 * window has no eligible cohort, and rendering 0% there would read as "this
 * product retains nobody" rather than "not enough time has passed".
 */
function ProductsPanel({
  products,
  error,
}: {
  products: AdminProductMetrics | null;
  error: string | null;
}) {
  const { t } = useTranslation("pages-core");

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          {t("AdminMetricsPage.productsHeading")}
        </Typography>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }
  if (!products) return null;

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        {t("AdminMetricsPage.productsHeading")}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        {t("AdminMetricsPage.productsPartialWarning", { days: products.return_window_days })}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
        {t("AdminMetricsPage.productsNoRevenueNote")}
      </Typography>

      {products.insufficient_elapsed_time ? (
        <Typography variant="body2" color="warning.main" data-testid="products-insufficient">
          {t("AdminMetricsPage.productsInsufficientTime", {
            days: products.return_window_days,
          })}
        </Typography>
      ) : products.products.length === 0 ? (
        <Typography variant="body2">{t("AdminMetricsPage.productsEmpty")}</Typography>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("AdminMetricsPage.productsProductColumn")}</TableCell>
                <TableCell align="right">{t("AdminMetricsPage.productsAcquiredColumn")}</TableCell>
                <TableCell align="right">{t("AdminMetricsPage.productsReturnedColumn")}</TableCell>
                <TableCell align="right">{t("AdminMetricsPage.productsRateColumn")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.products.map((p) => (
                <TableRow key={p.product}>
                  <TableCell>{p.product}</TableCell>
                  <TableCell align="right">
                    {p.acquired === null ? t("AdminMetricsPage.navSuppressed") : p.acquired}
                  </TableCell>
                  <TableCell align="right">
                    {p.returned === null ? t("AdminMetricsPage.navSuppressed") : p.returned}
                  </TableCell>
                  <TableCell align="right" data-testid={`product-rate-${p.product}`}>
                    {p.anomaly === "returned_exceeds_acquired" ? (
                      <Tooltip title={t("AdminMetricsPage.contentAnomalyTooltip")}>
                        <Typography
                          component="span"
                          variant="body2"
                          color="warning.main"
                          data-testid="product-anomaly"
                        >
                          {t("AdminMetricsPage.contentAnomaly")}
                        </Typography>
                      </Tooltip>
                    ) : p.return_rate_pct === null ? (
                      t("AdminMetricsPage.navSuppressed")
                    ) : (
                      `${p.return_rate_pct.toFixed(1)}%`
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

const RANGE_DAYS = [7, 30, 90] as const;
type RangeDays = (typeof RANGE_DAYS)[number];

/** Half-open [from, to) ending now, matching the API contract exactly. */
function rangeFor(days: RangeDays): AdminMetricsRange {
  const to = new Date();
  const from = new Date(to.getTime() - days * 86_400_000);
  return { from: from.toISOString(), to: to.toISOString() };
}

export default function AdminMetricsPage() {
  const { t, i18n } = useTranslation("pages-core");
  const [data, setData] = useState<AdminFunnelCounts | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState<RangeDays>(7);
  const [nav, setNav] = useState<AdminNavigationMetrics | null>(null);
  const [navError, setNavError] = useState<string | null>(null);
  const [content, setContent] = useState<AdminContentMetrics | null>(null);
  const [contentError, setContentError] = useState<string | null>(null);
  const [products, setProducts] = useState<AdminProductMetrics | null>(null);
  const [productsError, setProductsError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Same range object for both requests — computed once, so the funnel
      // and the navigation panel can never end up describing different
      // windows (see rangeFor's doc comment).
      const range = rangeFor(days);
      // Concurrent, not sequential: these two requests are independent, and
      // awaiting them in series added the navigation query's latency to first
      // paint for no reason. allSettled rather than all — one rejection must
      // not discard the other's result, which is the whole point of the
      // separate error states below.
      const [summaryRes, navRes, contentRes, productsRes] = await Promise.allSettled([
        getAdminMetricsSummary(range),
        getAdminNavigationMetrics(range),
        getAdminContentMetrics(range),
        getAdminProductMetrics(range),
      ]);
      if (cancelled) return;

      if (summaryRes.status === "fulfilled") {
        setData(summaryRes.value);
      } else {
        setError(t("AdminMetricsPage.metricsLoadErrorMessage"));
      }
      // A navigation-fetch failure degrades only its own panel, not the whole
      // page — the funnel above is more load-bearing.
      if (navRes.status === "fulfilled") {
        setNav(navRes.value);
      } else {
        setNavError(t("AdminMetricsPage.navLoadErrorMessage"));
      }
      if (contentRes.status === "fulfilled") {
        setContent(contentRes.value);
      } else {
        setContentError(t("AdminMetricsPage.contentLoadErrorMessage"));
      }
      if (productsRes.status === "fulfilled") {
        setProducts(productsRes.value);
      } else {
        setProductsError(t("AdminMetricsPage.productsLoadErrorMessage"));
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [t, days]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error ?? t("AdminMetricsPage.noDataMessage")}</Typography>
      </Box>
    );
  }

  const northStar: Metric = {
    label: t("AdminMetricsPage.northStarLabel"),
    value: String(data.artifacts_complete_live_site),
    hint: t("AdminMetricsPage.northStarHint"),
    highlight: true,
  };

  const funnel: Metric[] = [
    {
      label: t("AdminMetricsPage.totalMembersLabel"),
      value: String(data.total_users),
      hint: t("AdminMetricsPage.totalMembersHint", { count: data.new_users_7d }),
    },
    {
      label: t("AdminMetricsPage.activatedLabel"),
      value: `${data.activated_users} (${data.activation_rate_pct}%)`,
      hint: t("AdminMetricsPage.activatedHint"),
    },
    {
      label: t("AdminMetricsPage.progressingLabel"),
      value: String(data.progressing_7d),
      hint: t("AdminMetricsPage.progressingHint"),
    },
    {
      label: t("AdminMetricsPage.artifactsInProgressLabel"),
      value: String(data.artifacts_in_progress),
    },
    {
      label: t("AdminMetricsPage.artifactsCompleteLabel"),
      value: String(data.artifacts_complete),
      hint: t("AdminMetricsPage.artifactsCompleteHint", {
        rate: data.artifact_completion_rate_pct,
      }),
    },
    {
      label: t("AdminMetricsPage.payingMembersLabel"),
      value: String(data.paying_users),
      hint: t("AdminMetricsPage.payingMembersHint"),
    },
  ];

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: "auto" }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        {t("AdminMetricsPage.founderMetricsHeading")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("AdminMetricsPage.generatedAtNotice", {
          date: new Date(data.generated_at).toLocaleString(),
        })}
      </Typography>

      <Box
        sx={{
          mb: 1,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 2,
        }}
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          value={days}
          onChange={(_e, next) => {
            if (next !== null) setDays(next as RangeDays);
          }}
          aria-label={t("AdminMetricsPage.rangeGroupLabel")}
        >
          {RANGE_DAYS.map((d) => (
            <ToggleButton key={d} value={d} aria-label={t("AdminMetricsPage.rangeDaysOption", { count: d })}>
              {t("AdminMetricsPage.rangeDaysOption", { count: d })}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {/* Rendered from the server's own window_from/window_to, never from the
            client-side range: if the two ever disagree, the server is right and
            the founder needs to see what was actually measured. */}
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid="metrics-window"
        >
          {t("AdminMetricsPage.windowNotice", {
            from: new Date(data.window_from).toLocaleDateString(i18n.language),
            to: new Date(data.window_to).toLocaleDateString(i18n.language),
          })}
        </Typography>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 3 }}>
        {t("AdminMetricsPage.windowScopeNotice", {
          newUsers: data.new_users_in_window,
          active: data.progressing_in_window,
        })}
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <MetricCard m={northStar} />
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={2}>
        {funnel.map((m) => (
          <Grid item xs={12} sm={6} md={4} key={m.label}>
            <MetricCard m={m} />
          </Grid>
        ))}
      </Grid>

      <NavigationPanel nav={nav} error={navError} />
      <ContentPanel content={content} error={contentError} />
      <ProductsPanel products={products} error={productsError} />

      <OperationsPanel />
    </Box>
  );
}
